import { Dialog, Transition } from "@headlessui/react";
import moment from "moment";
import { Fragment, useEffect, useMemo, useState } from "react";
import CurrencyInput from "react-currency-input-field";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";

import success from "../../../assets/images/icon_invoice/success.svg";
import { isDarkColor } from "../../../config/DarkColorChecker";
import { formatCurrency } from "../../../config/global";
import {
  InvoiceDataItem,
  InvoiceHistoryDetailItem,
  PaymentSuccessData,
} from "./InvoiceWebTypes";
import { getDetailInvoiceHistory } from "../../../core/service/api_invoice";

type InvoicePaymentConfirmationDialogProps = {
  isOpen: boolean;
  t: TFunction;
  onClose: () => void;
  onConfirm: () => void;
};

type InvoiceCreditDetailModalProps = {
  show: boolean;
  active: string;
  dataTemp: InvoiceDataItem;
  dataTempHistory: InvoiceHistoryDetailItem[];
  userName?: string;
  accentColor?: string;
  cashLabel: string;
  language: string;
  t: TFunction;
  onClose: () => void;
  onPay: () => void;
};

type InvoicePaymentModalProps = {
  show: boolean;
  active: string;
  dataTemp: InvoiceDataItem;
  userName?: string;
  accentColor?: string;
  cashLabel: string;
  paidLabel: string;
  unpaidLabel: string;
  partialLabel: string;
  nominalLabel: string;
  minimumPayment: number;
  language: string;
  showDescription: boolean;
  isShowMore: boolean;
  t: TFunction;
  onAmountChange: (value?: string) => void;
  onPrimaryAction: () => void;
  onDownload: () => void;
  onClose: () => void;
  onToggleShowMore: () => void;
};

type InvoicePinModalProps = {
  show: boolean;
  onConfirm: () => void;
};

type InvoiceConfirmationModalProps = {
  show: boolean;
  onConfirm: () => void;
};

type InvoiceResultModalProps = {
  show: boolean;
  dataTemp: InvoiceDataItem;
  paymentSuccess: PaymentSuccessData;
  userName?: string;
  accentColor?: string;
  cashLabel: string;
  t: TFunction;
  onDownload: () => void;
  onClose: () => void;
};

const getStatusLabel = ({
  status,
  paidLabel,
  unpaidLabel,
  partialLabel,
}: {
  status?: string;
  paidLabel: string;
  unpaidLabel: string;
  partialLabel: string;
}) => {
  if (status === "PAID") return paidLabel;
  if (status === "UNPAID") return unpaidLabel;
  if (status === "PARTIAL") return partialLabel;

  return null;
};

export const InvoicePaymentConfirmationDialog = ({
  isOpen,
  t,
  onClose,
  onConfirm,
}: InvoicePaymentConfirmationDialogProps) => {
  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title
                  as="h3"
                  className="text-lg font-medium leading-6 text-gray-900"
                >
                  {t("INVOICE.confirm")}
                </Dialog.Title>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    {t("INVOICE.paymentConfirm")}
                  </p>
                </div>

                <div className="mt-4 flex gap-2 justify-center">
                  <button
                    type="button"
                    className="active:bg-zinc-100 inline-flex justify-center rounded-md border border-transparent bg-red-100 px-4 py-2 text-sm font-medium text-red-900 hover:bg-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
                    onClick={onClose}
                  >
                    {t("OTHER.no")}
                  </button>
                  <button
                    type="button"
                    className="active:bg-psp-hover active:text-white inline-flex justify-center rounded-md border border-transparent bg-blue-100 px-4 py-2 text-sm font-medium text-blue-900 hover:bg-blue-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                    onClick={onConfirm}
                  >
                    {t("OTHER.yes")}
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export const InvoiceCreditDetailModal = ({
  show,
  active,
  dataTemp,
  dataTempHistory,
  userName,
  accentColor,
  cashLabel,
  language,
  t,
  onClose,
  onPay,
}: InvoiceCreditDetailModalProps) => {
  const [detail, setDetail] = useState<any>({});
  const [isExpandedDescription, setIsExpandedDescription] = useState(false);
  const DESCRIPTION_LIMIT = 100;
  const isLongDescription = (dataTemp?.description?.length || 0) > DESCRIPTION_LIMIT;
  useEffect(() => {
    console.log(show);
    if (show) {
      setIsExpandedDescription(false);
      getDetailInvoiceHistory(dataTemp.invoiceId, dataTemp.transactionId).then(
        (res) => {
          console.log("detail", res.data);
          setDetail(res.data);
        },
      );
    }
  }, [show]);

  useEffect(() => {
    setIsExpandedDescription(false);
  }, [dataTemp?.description]);

  const getInvoiceStatus = () => {
    if (dataTemp.status === "PAID") return t("INVOICE.lunas");
    if (dataTemp.status === "UNPAID") return t("INVOICE.unpaid");
    if (dataTemp.status === "PARTIAL") return t("INVOICE.partial");
    return dataTemp.status;
  };
  const isHasDetailPayment = useMemo(() => {
    if (dataTemp && dataTemp.reference && dataTemp?.reference.length) {
      return dataTemp?.reference.some((item: any) => item.detailId !== "");
    }
    return false;
  }, [dataTemp]);
  return (
    <Transition
      show={show}
      enter="transition-opacity duration-200"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-150"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <>
        <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
          <div className="relative w-full max-w-5xl mx-auto">
            <div className="border-0 rounded-2xl shadow-lg bg-white outline-none focus:outline-none">
              <div className="flex justify-between items-center px-6 pt-6">
                <div className="font-bold text-xl">{t("INVOICE.title")}</div>
                {dataTemp?.detail?.some((detail) => detail.discount !== 0) && (
                  <span className="bg-red-500 text-white px-3 py-1 rounded-full text-xs ml-2">
                    Discount
                  </span>
                )}
                <button
                  className="text-2xl font-bold text-gray-400 hover:text-gray-600 ml-auto"
                  onClick={onClose}
                >
                  &times;
                </button>
              </div>
              <div className="flex flex-row px-6 py-4 gap-6">
                <div className="flex-1 flex flex-col items-center justify-center border-r pr-6">
                  <div className="text-slate-500 text-sm mb-2">
                    {t("INVOICE.paid")}
                  </div>
                  <div className="text-4xl font-bold mb-4">
                    Rp{" "}
                    {formatCurrency(
                      active === "all"
                        ? dataTemp.paidAmount
                        : detail.paidAmount || 0,
                    )
                      .replace("Rp", "")
                      .trim()}
                  </div>
                  <div className="flex justify-between w-full text-center mt-4">
                    <div>
                      <div className="text-slate-400 text-xs">
                        {t("INVOICE.sisa")}
                      </div>
                      <div className="font-semibold text-base text-red">
                        Rp{" "}
                        {formatCurrency(
                          active === "all"
                            ? (dataTemp.amount || 0) -
                                (dataTemp.paidAmount || 0)
                            : detail.remainingAmount || 0,
                        )
                          .replace("Rp", "")
                          .trim()}
                      </div>
                    </div>
                    <div>
                      <div className="text-slate-400 text-xs">
                        {t("INVOICE.amountLabel")}
                      </div>
                      <div className="font-semibold text-base">
                        Rp{" "}
                        {formatCurrency(
                          active === "all"
                            ? dataTemp.amount
                            : detail.invoiceAmount || 0,
                        )
                          .replace("Rp", "")
                          .trim()}
                      </div>
                    </div>
                  </div>
                </div>
                {/* <pre>{JSON.stringify(dataTemp, null, 2)}</pre> */}
                <div className="flex-1 flex flex-col gap-2 height-[55vh] max-h-[55vh] overflow-y-auto">
                  <div className="font-semibold mb-1 text-lg">
                    {t("INVOICE.paymentData")}
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <div className="w-1/2">{t("INVOICE.parentName")}</div>
                      <div className="w-1/2 text-right">{userName}</div>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <div className="w-1/2">{t("INVOICE.studentName")}</div>
                      <div className="w-1/2 text-right">
                        {dataTemp?.callerName}
                      </div>
                    </div>
                  </div>
                  <div className="font-semibold mb-1 text-lg">
                    {t("INVOICE.detailInvoice")}
                  </div>
                  <div>
                    <div className="flex justify-between items-center mb-2 w-full">
                      <span className="w-1/2">{t("INVOICE.invoiceName")}</span>
                      <span className="font-normal w-1/2">
                        {dataTemp?.title}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span>{t("INVOICE.date")}</span>
                      <span className="font-normal">
                        {moment(dataTemp?.invoiceDate)
                          .locale(language)
                          .format("LL")}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span>{t("INVOICE.limit")}</span>
                      <span className="font-normal">
                        {moment(dataTemp?.dueDate)
                          .locale(language)
                          .format("LL")}
                      </span>
                    </div>
                    <div className="flex justify-between items-center mb-2">
                      <span>{t("INVOICE.invoiceType")}</span>
                      <span className="font-normal">
                        {dataTemp?.partialMethod === false
                          ? cashLabel
                          : t("INVOICE.credit")}
                      </span>
                    </div>
                    <div className="mb-2">
                      <span>Description</span>
                    </div>
                    {!dataTemp?.description?.trim() ? (
                      <div className="text-sm text-slate-500 italic">-</div>
                    ) : (
                      <div>
                        <div className="text-sm text-justify break-words whitespace-pre-wrap">
                          {isLongDescription && !isExpandedDescription
                            ? dataTemp.description.substring(
                                0,
                                DESCRIPTION_LIMIT,
                              ) + "..."
                            : dataTemp.description}
                        </div>
                        {isLongDescription && (
                          <button
                            onClick={() =>
                              setIsExpandedDescription(!isExpandedDescription)
                            }
                            aria-expanded={isExpandedDescription}
                            className="text-xs font-medium text-blue-600 underline mt-1"
                          >
                            {isExpandedDescription
                              ? t("INVOICE.hideDescription")
                              : t("INVOICE.showAllDescription")}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="font-semibold mb-1 text-lg">
                    {t("INVOICE.summary")}
                  </div>
                  <div className="flex justify-between items-center mb-2">
                    <span>Status</span>
                    <span className="font-normal">{getInvoiceStatus()}</span>
                  </div>
                  {isHasDetailPayment && (
                    <>
                      {dataTemp?.detail?.map((item, index) => (
                        <>
                          <div
                            key={index}
                            className="flex justify-between items-center w-full"
                          >
                            <span className="w-1/2">{item.title}</span>
                            <span className="font-normal w-1/2 text-right">
                              {formatCurrency(
                                (item.amount || 0) - (item.discount || 0),
                              )}{" "}
                              <br />
                              {item.transactionAmount !== 0 && (
                                <span className="text-sm">
                                  ({t("INVOICE.paid")}{" "}
                                  {formatCurrency(item.transactionAmount)})
                                </span>
                              )}
                            </span>
                            {/* <pre>{JSON.stringify(item, null, 2)}</pre> */}
                          </div>
                          <hr />
                        </>
                      ))}
                    </>
                  )}

                  <div className="flex justify-between items-center mb-2">
                    <span>{t("INVOICE.total")}</span>
                    <span className="font-normal">
                      {formatCurrency(
                        active === "all"
                          ? dataTemp.amount
                          : detail.invoiceAmount || 0,
                      )}
                    </span>
                  </div>
                  {isHasDetailPayment && (
                    <div className="flex justify-between items-center mb-2">
                      <span>{t("INVOICE.totalPrevPaid")}</span>
                      <span className="font-normal">
                        {formatCurrency(detail.prevPaidAmount)}
                      </span>
                    </div>
                  )}

                  <div className="flex justify-between items-center mb-2">
                    <span>{t("INVOICE.totalpay")}</span>
                    <span className="font-normal">
                      {formatCurrency(
                        active === "all"
                          ? dataTemp.paidAmount
                          : detail.paidAmount || 0,
                      )}
                    </span>
                  </div>
                  {isHasDetailPayment && (
                    <div className="flex justify-between items-center mb-2">
                      <span>{t("INVOICE.sisa")}</span>
                      <span className="font-normal">
                        {formatCurrency(detail.remainingAmount)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
              <div className="px-6 pb-6 flex justify-end">
                {active === "history" ? (
                  <button
                    style={{ backgroundColor: accentColor }}
                    onClick={onClose}
                    className={`px-8 py-3 rounded-lg text-lg font-semibold shadow ${
                      isDarkColor(accentColor) ? "text-white" : "text-black"
                    }`}
                  >
                    {t("HOME.close") || "Tutup"}
                  </button>
                ) : (
                  <button
                    style={{ backgroundColor: accentColor }}
                    onClick={onPay}
                    className={`px-8 py-3 rounded-lg text-lg font-semibold shadow ${
                      isDarkColor(accentColor) ? "text-white" : "text-black"
                    }`}
                  >
                    {t("INVOICE.pay") || "Bayar Sekarang"}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="opacity-25 fixed inset-0 z-40 bg-black" />
      </>
    </Transition>
  );
};

export const InvoicePaymentModal = ({
  show,
  active,
  dataTemp,
  userName,
  accentColor,
  cashLabel,
  paidLabel,
  unpaidLabel,
  partialLabel,
  nominalLabel,
  minimumPayment,
  language,
  showDescription,
  isShowMore,
  t,
  onAmountChange,
  onPrimaryAction,
  onDownload,
  onClose,
  onToggleShowMore,
}: InvoicePaymentModalProps) => {
  return (
    <Transition
      show={show}
      enter="transition-opacity duration-200"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-150"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <>
        <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
          <div className="relative w-1/3 mx-10 flex">
            <div className="border-0 rounded-2xl shadow-lg min-w-[30vw] h-auto max-h-[95vh] overflow-y-auto overflow-x-hidden bg-white outline-none focus:outline-none">
              <div className="relative py-6 px-6 flex-auto" id="Detail">
                <div className="flex flex-row w-full h-auto">
                  <div className="font-montserrat text-slate-500 flex relative flex-col w-full">
                    {active !== "all" ? (
                      <div className="w-full">
                        <img src={success} alt="" className="flex mx-auto" />
                        <p className="text-2xl text-center font-bold mt-4">
                          {t("INVOICE.success")}
                        </p>
                      </div>
                    ) : null}
                    <p className="font-bold text-center">{dataTemp?.title}</p>
                    {dataTemp?.description !== "" && showDescription && (
                      <div>
                        {dataTemp?.description && (
                          <div
                            className="font-poppins font-normal text-sm text-psp-nav text-justify"
                            dangerouslySetInnerHTML={{
                              __html:
                                (dataTemp?.description?.length || 0) > 100 &&
                                !isShowMore
                                  ? dataTemp?.description
                                      .replaceAll(/\n/g, "<br>")
                                      .substring(0, 100) + "..."
                                  : dataTemp?.description.replaceAll(
                                      /\n/g,
                                      "<br>",
                                    ),
                            }}
                          />
                        )}

                        <h1 className="font-poppins font-normal text-sm text-psp-nav text-justify">
                          {(dataTemp?.description?.length || 0) > 100 && (
                            <span
                              className="text-blue-600 underline cursor-pointer"
                              onClick={onToggleShowMore}
                            >
                              {isShowMore
                                ? t("INVOICE.showLess")
                                : t("INVOICE.showMore")}
                            </span>
                          )}
                        </h1>
                      </div>
                    )}
                    <p className="font-bold">{t("INVOICE.dataInvoice")}</p>
                    <div
                      className={`w-full relative flex flex-row justify-between ${
                        userName === dataTemp?.callerName ? "hidden" : ""
                      }`}
                    >
                      <span className="my-2 font-montserrat">
                        {t("INVOICE.parentName")}
                      </span>
                      <span className="my-2 font-montserrat font-semibold">
                        {userName}
                      </span>
                    </div>
                    <div className="w-full relative flex flex-row justify-between">
                      <span className="my-2 font-montserrat w-1/2">
                        {t("INVOICE.studentName")}
                      </span>
                      <span className="my-2 font-montserrat font-semibold text-right w-1/2">
                        {dataTemp?.callerName}
                      </span>
                    </div>
                    <div className="w-full relative flex flex-row justify-between">
                      <span className="w-1/2 my-2 font-montserrat font-semibold">
                        {t("INVOICE.detailInvoice")}
                      </span>
                    </div>
                    <div className="w-full relative flex flex-row justify-between">
                      <span className="w-1/2 my-2 font-montserrat">
                        {t("INVOICE.invoiceName")}
                      </span>
                      <span className="w-1/2 my-2 font-montserrat font-semibold text-right">
                        {dataTemp?.title}
                      </span>
                    </div>
                    <div className="w-full relative flex flex-row justify-between">
                      <span className="w-1/2 my-2 font-montserrat">
                        {t("INVOICE.date")}
                      </span>
                      <span className="w-1/2 my-2 font-montserrat font-semibold text-right">
                        {moment(dataTemp?.invoiceDate)
                          .locale(language)
                          .format("LL")}
                      </span>
                    </div>
                    <div className="w-full relative flex flex-row justify-between">
                      <span className="w-1/2 my-2 font-montserrat">
                        {t("INVOICE.limit")}
                      </span>
                      <span className="w-1/2 my-2 font-montserrat font-semibold text-right">
                        {moment(dataTemp?.dueDate)
                          .locale(language)
                          .format("LL")}
                      </span>
                    </div>
                    <div className="w-full relative flex flex-row justify-between">
                      <span className="w-1/2 my-2 font-montserrat">
                        {t("INVOICE.invoiceType")}
                      </span>
                      <span className="w-1/2 my-2 font-montserrat font-semibold text-right">
                        {dataTemp?.partialMethod === false
                          ? cashLabel
                          : t("INVOICE.credit")}
                      </span>
                    </div>
                    <div className="w-full relative flex flex-row justify-between border-b-2">
                      <span className="w-1/2 my-2 font-montserrat">
                        {t("MUTATION.status")}
                      </span>
                      <span className="w-1/2 my-2 font-montserrat font-semibold text-right">
                        {getStatusLabel({
                          status: dataTemp?.status,
                          paidLabel,
                          unpaidLabel,
                          partialLabel,
                        })}
                      </span>
                    </div>
                    {dataTemp?.detail?.map((detail, index) => (
                      <div
                        key={index}
                        className="w-full relative flex flex-row justify-between items-center my-1"
                        hidden={!dataTemp?.showDetail}
                      >
                        <span>{detail.title}</span>
                        <span className="my-1 font-montserrat font-semibold">
                          {formatCurrency(detail.amount)}
                        </span>
                      </div>
                    ))}
                    <div className="w-full relative flex flex-row justify-between">
                      <span className="my-2 font-montserrat">
                        {t("INVOICE.totalpay")}
                      </span>
                      <span className="my-2 font-montserrat font-semibold">
                        {formatCurrency(dataTemp?.amount)}
                      </span>
                    </div>
                    <div className="w-full relative flex flex-row justify-between">
                      <span className="my-2 font-montserrat">
                        {t("INVOICE.paid")}
                      </span>
                      <span className="my-2 font-montserrat font-semibold">
                        {formatCurrency(dataTemp?.paidAmount)}
                      </span>
                    </div>
                    <div className="w-full relative flex flex-row justify-between">
                      <span className="my-2 font-montserrat">
                        {t("INVOICE.minus")}
                      </span>
                      <span className="my-2 font-montserrat font-semibold">
                        {formatCurrency(dataTemp?.remainingAmount || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              <div className="px-3 pb-3">
                <div>
                  {dataTemp?.partialMethod !== false ? (
                    <form>
                      <CurrencyInput
                        className="px-3 py-3 mt-3 rounded-xl h-auto border-1 border-psp-border-gray border-solid active:border-0 w-full"
                        placeholder={`${nominalLabel} ${formatCurrency(
                          minimumPayment,
                          `${language}-${language.toUpperCase()}`,
                        )}`}
                        hidden={active !== "all"}
                        decimalsLimit={2}
                        prefix="Rp "
                        value={
                          (dataTemp?.amount || 0) -
                            (dataTemp?.paidAmount || 0) <=
                          minimumPayment
                            ? (dataTemp?.amount || 0) -
                              (dataTemp?.paidAmount || 0)
                            : undefined
                        }
                        disabled={
                          (dataTemp?.amount || 0) -
                            (dataTemp?.paidAmount || 0) <=
                          minimumPayment
                        }
                        onValueChange={(value) => onAmountChange(value)}
                      />
                    </form>
                  ) : (
                    <form hidden>
                      <CurrencyInput
                        className="px-3 py-3 mt-3 rounded-xl h-auto border-1 border-psp-border-gray border-solid active:border-0 w-full"
                        placeholder={`${nominalLabel} ${formatCurrency(
                          minimumPayment,
                          `${language}-${language.toUpperCase()}`,
                        )}`}
                        hidden={active !== "all"}
                        decimalsLimit={2}
                        prefix="Rp "
                        onValueChange={(value) => onAmountChange(value)}
                      />
                    </form>
                  )}

                  {active === "all" ? (
                    <button
                      style={{ backgroundColor: accentColor }}
                      onClick={onPrimaryAction}
                      className={`w-full py-3 rounded-lg mt-3 ${
                        isDarkColor(accentColor) ? "text-white" : "text-black"
                      }`}
                    >
                      {t("INVOICE.pay")}
                    </button>
                  ) : (
                    <button
                      style={{ backgroundColor: accentColor }}
                      onClick={onDownload}
                      className={`w-full py-3 rounded-lg mt-3 ${
                        isDarkColor(accentColor) ? "text-white" : "text-black"
                      }`}
                    >
                      {t("INVOICE.download")}
                    </button>
                  )}
                  <button
                    onClick={onClose}
                    className="w-full text-white bg-red py-3 rounded-lg mt-3"
                  >
                    {t("HOME.close")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="opacity-25 fixed inset-0 z-40 bg-black" />
      </>
    </Transition>
  );
};

export const InvoicePinModal = ({ show, onConfirm }: InvoicePinModalProps) => {
  const { t } = useTranslation();

  return (
    <Transition
      show={show}
      enter="transition-opacity duration-200"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-150"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <>
        <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
          <div className="relative w-auto my-6 mx-auto max-w-3xl">
            <div className="border-0 rounded-2xl shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
              <div className="relative p-6 flex-auto">
                <div>
                  <div className="flex pt-2">
                    <div className="w-full">
                      <p className="font-bold">Input Pin</p>
                      <form>
                        <input className="w-full py-2 border rounded-lg px-3 mb-3" />
                      </form>
                    </div>
                  </div>
                  <div className="flex flex-row gap-3">
                    <button className="w-full text-white bg-red py-2.5 px-5 rounded-lg">
                      {t("OTHER.no")}
                    </button>
                    <button
                      onClick={onConfirm}
                      className="w-full text-white bg-psp-btn-primary py-2.5 px-5 rounded-lg"
                    >
                      {t("OTHER.yes")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="opacity-25 fixed inset-0 z-40 bg-black" />
      </>
    </Transition>
  );
};

export const InvoiceConfirmationModal = ({
  show,
  onConfirm,
}: InvoiceConfirmationModalProps) => {
  const { t } = useTranslation();

  return (
    <Transition
      show={show}
      enter="transition-opacity duration-200"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-150"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <>
        <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
          <div className="relative w-auto my-6 mx-auto max-w-3xl">
            <div className="border-0 rounded-2xl shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
              <div className="relative p-6 flex-auto">
                <div>
                  <div className="flex pt-2 mb-3">
                    <div className="w-full">
                      <p className="font-bold text-xl">
                        {t("INVOICE.confirm")}
                      </p>
                      <span>{t("INVOICE.paymentConfirm")}</span>
                    </div>
                  </div>
                  <div className="flex flex-row gap-3">
                    <button className="w-full text-white bg-red py-2.5 px-5 rounded-lg">
                      {t("OTHER.no")}
                    </button>
                    <button
                      onClick={onConfirm}
                      className="w-full text-white bg-psp-btn-primary py-2.5 px-5 rounded-lg"
                    >
                      {t("OTHER.yes")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="opacity-25 fixed inset-0 z-40 bg-black" />
      </>
    </Transition>
  );
};

export const InvoiceResultModal = ({
  show,
  dataTemp,
  paymentSuccess,
  userName,
  accentColor,
  cashLabel,
  t,
  onDownload,
  onClose,
}: InvoiceResultModalProps) => {
  return (
    <Transition
      show={show}
      enter="transition-opacity duration-200"
      enterFrom="opacity-0"
      enterTo="opacity-100"
      leave="transition-opacity duration-150"
      leaveFrom="opacity-100"
      leaveTo="opacity-0"
    >
      <>
        <div className="justify-center items-center flex fixed inset-0 z-50 outline-none focus:outline-none">
          <div className="relative w-auto min-w-[30vw] h-auto max-h-[95vh] overflow-y-auto overflow-x-hidden my-6 mx-auto">
            <div className="border-0 rounded-2xl shadow-lg relative flex flex-col bg-white outline-none focus:outline-none">
              <div
                id="DetailPay"
                className="relative px-6 pt-6 flex-auto w-auto"
              >
                <div className="flex flex-col">
                  <div className="w-full">
                    <img src={success} alt="" className="flex mx-auto" />
                  </div>
                  <div className="text-center">
                    <p className="text-2xl font-bold mt-4">
                      {t("INVOICE.success")}
                    </p>
                    <span>
                      {t("MESSAGE.date")} :{" "}
                      {moment(paymentSuccess?.dateTime).format("DD MMMM YYYY")}
                    </span>
                    <br />
                    <span>
                      {moment(paymentSuccess?.dateTime).format("HH : mm")}
                    </span>
                  </div>
                </div>
                <div className="text-[1vw] mt-4">
                  <div className="w-full relative flex flex-row justify-between">
                    <span className="my-2 font-montserrat font-semibold">
                      {t("INVOICE.dataInvoice")}
                    </span>
                  </div>
                  <div
                    className={`w-full relative flex flex-row justify-between ${
                      userName === dataTemp?.callerName ? "hidden" : ""
                    }`}
                  >
                    <span className="my-2 font-montserrat">
                      {t("INVOICE.parentName")}
                    </span>
                    <span className="my-2 font-montserrat font-semibold">
                      {userName}
                    </span>
                  </div>
                  <div className="w-full relative flex flex-row justify-between">
                    <span className="my-2 font-montserrat w-1/2">
                      {t("INVOICE.studentName")}
                    </span>
                    <span className="my-2 font-montserrat font-semibold text-right w-1/2">
                      {dataTemp?.callerName}
                    </span>
                  </div>
                  <div className="w-full relative flex flex-row justify-between">
                    <span className="my-2 font-montserrat font-semibold">
                      {t("INVOICE.detailInvoice")}
                    </span>
                  </div>
                  <div className="w-full relative flex flex-row justify-between">
                    <span className="my-2 font-montserrat">
                      {t("INVOICE.invoiceName")}
                    </span>
                    <span className="my-2 font-montserrat font-semibold text-right">
                      {dataTemp?.title}
                    </span>
                  </div>
                  <div className="w-full relative flex flex-row justify-between">
                    <span className="my-2 font-montserrat">
                      {t("INVOICE.invoiceType")}
                    </span>
                    <span className="my-2 font-montserrat font-semibold">
                      {dataTemp?.partialMethod === false
                        ? cashLabel
                        : t("INVOICE.credit")}
                    </span>
                  </div>
                  <div className="w-full relative flex flex-row justify-between">
                    <span className="my-2 font-montserrat text-white">
                      {t("INVOICE.invoiceType")}
                    </span>
                    <span className="my-2 font-montserrat font-semibold text-blue-400">
                      {t("INVOICE.success")}
                    </span>
                  </div>
                  {dataTemp?.detail?.map((detail, index) => (
                    <div
                      key={index}
                      className="w-full relative flex flex-row justify-between items-center my-1"
                      hidden={!dataTemp?.showDetail}
                    >
                      <span>{detail.title}</span>
                      <span className="my-1 font-montserrat font-semibold text-blue-400">
                        {formatCurrency(detail.amount)}
                      </span>
                    </div>
                  ))}
                  <div className="w-full relative flex flex-row justify-between">
                    <span className="my-2 font-montserrat">
                      {t("INVOICE.totalpay")}
                    </span>
                    <span className="my-2 font-montserrat font-semibold text-blue-400">
                      {formatCurrency(
                        paymentSuccess?.inquiryResponseDto?.amount,
                      )}
                    </span>
                  </div>
                  <div className="w-full relative flex flex-row justify-between border-t mb-6">
                    <span className="my-2 font-montserrat">
                      {t("INVOICE.minus")}
                    </span>
                    <span className="my-2 font-montserrat font-semibold">
                      {formatCurrency(
                        (paymentSuccess?.inquiryResponseDto?.amount || 0) -
                          ((paymentSuccess?.inquiryResponseDto?.paidAmount ||
                            0) +
                            (paymentSuccess?.amount || 0)),
                      )}
                    </span>
                  </div>
                </div>
              </div>
              <div className="px-6 pb-6">
                <div>
                  <button
                    onClick={onDownload}
                    style={{ backgroundColor: accentColor }}
                    className={`w-full py-2.5 rounded-full mt-3 ${
                      isDarkColor(accentColor) ? "text-white" : "text-black"
                    }`}
                  >
                    {t("INVOICE.download")}
                  </button>
                  <button
                    onClick={onClose}
                    className="w-full text-white bg-red py-2.5 rounded-full mt-3"
                  >
                    {t("OTHER.close")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="opacity-25 fixed inset-0 z-40 bg-black" />
      </>
    </Transition>
  );
};
